import { useState, useEffect, useCallback } from 'react';
import { rentalApi } from '../api/rentalApi';

/**
 * Custom Hook to manage the logic, state, and calculations for the Rental Wizard.
 * 
 * @param {Object} props - Hook properties.
 * @param {Function} props.onSuccess - Callback triggered after successful rental creation.
 * @returns {Object} State and handler functions for the rental wizard.
 */
export default function useRentalWizard({ onSuccess }) {
    const [step, setStep] = useState(1);
    const [availableItems, setAvailableItems] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [allRentals, setAllRentals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [rental, setRental] = useState({
        rental_number: '',
        customer_id: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        billing_mode: 'days',
        hours_per_day: 8,
        vat_rate: 19,
        location: '',
        handover_condition: 'Einwandfrei, wie besichtigt.',
        category: 'vehicle'
    });

    const stepTitles = [
        "Vorgang & Bereich",
        "Objektauswahl",
        "Zeitraum & Ort",
        "Mieter wählen",
        "Zustand & Dokumentation",
        "Rechnungsdaten",
        "Abschluss"
    ];

    const [rentalItems, setRentalItems] = useState([]);
    const [showCustomerAdd, setShowCustomerAdd] = useState(false);
    const [newCustomer, setNewCustomer] = useState({ company_name: '', email: '', phone: '', address: '', contact_person: '' });

    // Initial Data Fetching
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [itemsData, customersData, rentalsData] = await Promise.all([
                    rentalApi.getItems(),
                    rentalApi.getCustomers(),
                    rentalApi.getRentals()
                ]);
                setAvailableItems(itemsData);
                setCustomers(customersData);
                setAllRentals(rentalsData);
                
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const prefix = `${year}-${month}-N`;
                
                let maxN = 0;
                rentalsData.forEach(r => {
                    if (r.rental_number?.startsWith(prefix)) {
                        const num = parseInt(r.rental_number.replace(prefix, ''), 10);
                        if (!isNaN(num) && num > maxN) maxN = num;
                    }
                });
                
                setRental(prev => ({
                    ...prev,
                    rental_number: `${prefix}${maxN + 1}`
                }));

            } catch (err) {
                console.error('RENTAL_WIZARD_INIT_ERROR:', err);
                setError('Stammdaten konnten nicht geladen werden.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleAddInventoryItem = (item) => {
        const exists = rentalItems.find(si => si.id === item.id);
        if (exists) {
            setRentalItems([]);
            return;
        }
        setRentalItems([{ 
            id: item.id, 
            name: item.name, 
            quantity: 1, 
            unit_price_net: item.unit_price_net || 0, 
            pricing_type: item.pricing_type || 'day',
            vat_rate: 19,
            is_inventory: true
        }]);
    };

    const handleAddManualItem = () => {
        setRentalItems(prev => [...prev, { 
            id: null, 
            name: 'Manuelle Zusatzposition', 
            quantity: 1, 
            unit_price_net: 0, 
            pricing_type: 'flat',
            vat_rate: 19,
            is_inventory: false
        }]);
    };

    const updateItem = (idx, field, value) => {
        const newItems = [...rentalItems];
        let finalValue = value;
        if (field === 'unit_price_net') {
            finalValue = Math.max(0, parseFloat(value) || 0);
        }
        newItems[idx][field] = finalValue;
        setRentalItems(newItems);
    };

    const calculateItemTotals = (item) => {
        const qty = parseFloat(item.quantity) || 1;
        const price = parseFloat(item.unit_price_net) || 0;
        
        let multiplier = 1;
        if (item.pricing_type !== 'flat') {
            const start = new Date(rental.start_date);
            const end = rental.end_date ? new Date(rental.end_date) : start;
            const diffTime = end.getTime() - start.getTime();
            const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
            
            if (rental.billing_mode === 'days') {
                multiplier = days;
            } else if (rental.billing_mode === 'hours') {
                const hpd = parseFloat(rental.hours_per_day) || 8;
                multiplier = days * hpd;
            }
        }
        
        const net = qty * price * multiplier;
        return { net, multiplier };
    };

    const calculateGrandTotals = useCallback(() => {
        let totalNet = 0;
        rentalItems.forEach(item => {
            const { net } = calculateItemTotals(item);
            totalNet += net;
        });
        const vatRate = parseFloat(rental.vat_rate) || 0;
        const totalVat = totalNet * (vatRate / 100);
        return { totalNet, totalVat, totalBrutto: totalNet + totalVat };
    }, [rentalItems, rental.start_date, rental.end_date, rental.billing_mode, rental.hours_per_day, rental.vat_rate]);

    const handleCreate = async () => {
        if (!rental.rental_number) {
            setError('Bitte vergeben Sie eine Vorgangsnummer (Schritt 1).');
            return;
        }
        setError(null);
        setSubmitting(true);
        const { totalNet, totalVat, totalBrutto } = calculateGrandTotals();
        const payload = {
            ...rental,
            total_net: totalNet,
            total_vat: totalVat,
            total_brutto: totalBrutto,
            vat_rate: rental.vat_rate,
            line_items: rentalItems.map(item => ({
                rental_item_id: item.id,
                name: item.name,
                quantity: item.quantity || 1,
                pricing_type: item.pricing_type,
                unit_price_net: item.unit_price_net,
                total_net: calculateItemTotals(item).net
            }))
        };
        try {
            await rentalApi.createRental(payload);
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error('CREATE_RENTAL_FAIL:', err);
            const detail = err.response?.data?.error || err.message;
            setError(`Konnte nicht speichern: ${detail}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddCustomer = async () => {
        try {
            const res = await rentalApi.createCustomer(newCustomer);
            setCustomers(prev => [...prev, res]);
            setRental(prev => ({ ...prev, customer_id: res.id }));
            setShowCustomerAdd(false);
            setNewCustomer({ company_name: '', email: '', phone: '', address: '', contact_person: '' });
        } catch (e) {
            setError('Kunde konnte nicht angelegt werden.');
        }
    };

    return {
        step, setStep,
        stepTitles,
        availableItems,
        customers,
        allRentals,
        loading,
        submitting,
        error, setError,
        rental, setRental,
        rentalItems, setRentalItems,
        showCustomerAdd, setShowCustomerAdd,
        newCustomer, setNewCustomer,
        handleAddInventoryItem,
        handleAddManualItem,
        updateItem,
        calculateItemTotals,
        calculateGrandTotals,
        handleCreate,
        handleAddCustomer
    };
}
